<template>
  <SettingsPageLayout>
    <div>
      <Card>
        <div class="md:grid md:grid-cols-3">
          <div class="md:col-span-1 flex flex-col">
            <CardHeader>
              <CardTitle>Email</CardTitle>
              <CardDescription>
                Easily send emails to your candidates and teammates without leaving Humaniz.
              </CardDescription>
            </CardHeader>
          </div>
          <CardContent class="md:col-span-2">
            <div class="space-y-6">
              <template v-if="user?.postmarkEmailVerified">
                <Badge variant="green">Verified</Badge>
                <p class="text-sm leading-normal text-gray-500">
                  Your email has been verified and is ready to send email from Humaniz! Make sure to set your signature
                  below for consistency and the highest deliverability rates.
                </p>
                <div class="bg-gray-50 px-4 py-6 sm:px-6">
                  <QuillInput
                    v-model="user.emailSignature"
                    name="new-signature"
                    label="New Signature"
                    :label-sr-only="true"
                    size="large"
                  />
                </div>
              </template>
              <template v-else>
                <Badge variant="red">Unverified</Badge>
                <p class="text-sm leading-normal text-gray-500">
                  Your email is currently unverified for sending Email directly from Humaniz. Please verify your email
                  using the link sent to you from Postmark. If you didn't receive a link, use the Verify Email button
                  below to receive a new link from Postmark.
                </p>
              </template>
            </div>
          </CardContent>
        </div>
      </Card>
      <div class="mt-4 flex justify-end">
        <Button v-if="user?.postmarkEmailVerified" :disabled="saving" @click="updateUser">
          <LocalLoadingSpinner v-if="saving" />
          Save
        </Button>
        <Button v-else :disabled="verifying" @click="verifyEmail">
          <LocalLoadingSpinner v-if="verifying" />
          Verify Email
        </Button>
      </div>
    </div>
  </SettingsPageLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useCurrentUserStore } from '@/stores/currentUser';
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import QuillInput from '../../shared/QuillInput.vue';
import LocalLoadingSpinner from '../../shared/LocalLoadingSpinner.vue';
import { toast } from 'vue-sonner';
import { Api } from '@/api';

const currentUserStore = useCurrentUserStore();

const saving = ref(false);
const verifying = ref(false);

const user = computed(() => currentUserStore.currentUser);

const verifyEmail = async () => {
  verifying.value = true;

  try {
    const response = await Api.Users.verifyUserEmail();
    if (response.status === 'success') {
      toast.success('Email verification sent', { description: response.message });
      currentUserStore.loadCurrentUser();
    } else {
      toast.error('Failed to Verify Email', { description: response.message });
    }
  } finally {
    verifying.value = false;
  }
};

const updateUser = async () => {
  saving.value = true;

  const userId = user.value.id;
  if (!userId) return;

  try {
    const response = await Api.Users.updateUser(
      { id: `${userId}`, publishError: false },
      { updatedUserAttributes: { email_signature: user.value.emailSignature } }
    );

    if (response.status === 'success') {
      toast.success('Email settings updated', { description: 'Your Email settings were successfully updated.' });
      currentUserStore.loadCurrentUser();
    } else {
      toast.error('Failed to Update User');
    }
  } finally {
    saving.value = false;
  }
};
</script>

<style>
@reference "tailwindcss";
.user-generated-content {
  font-size: 12px;
}
.user-generated-content h1 {
  font-size: 36.62px;
  line-height: 36.62px;
  @apply mt-2 mb-1;
}
.user-generated-content h2 {
  font-size: 29.3px;
  line-height: 29.3px;
  @apply mt-2 mb-1;
}
.user-generated-content h3 {
  font-size: 23.44px;
  line-height: 23.44px;
  @apply mt-2 mb-1;
}
.user-generated-content h4 {
  font-size: 18.75px;
  line-height: 18.75px;
  @apply mt-2 mb-1;
}
.user-generated-content strong {
  font-weight: bold;
}
.user-generated-content em {
  font-style: italic;
}
.user-generated-content a {
  @apply underline text-indigo-500;
}
.user-generated-content ol {
  @apply list-decimal ml-4 mt-2;
}
.user-generated-content ul {
  @apply list-disc ml-4 mt-2;
}
</style>
